import type { ReactNode } from 'react';

import type { ButtonOrLinkOwnProps } from '#/components/ButtonOrLink/ButtonOrLink';
import type { ButtonOrLinkDataProps } from '#/components/ButtonOrLink/ButtonOrLinkData';
import { Icon } from '#/components/Icon/Icon';
import type { IconName } from '#/components/Icon/IconName';
import type { Color } from '#/theme/theme';

import { ButtonIconWrapper, StyledButtonOrLink } from './Button.styled';
import type { ButtonSize, ButtonVariant } from './Button.types';

export type ButtonOwnProps = ButtonOrLinkOwnProps & {
  icon?:
    | {
        name: IconName;
        color?: Color;
        size?: number;
        position?: 'left' | 'right';
      }
    | ReactNode;
  size?: ButtonSize;
  variant: ButtonVariant;
  width?: number;
  fullWidth?: boolean;
  height?: number;
  noPadding?: boolean;
  noBold?: boolean;
  rounded?: boolean;
};

type Props = ButtonOrLinkDataProps<ButtonOwnProps>;

export function Button({
  id,
  className,
  children,
  icon,
  size,
  tabIndex,
  variant,
  width,
  fullWidth,
  height,
  noPadding,
  noBold,
  title,
  rounded,
  qaId,
  'aria-label': ariaLabel,
  style,
  ...otherProps
}: Props) {
  const data = 'data' in otherProps ? otherProps.data : otherProps;
  const hideObjectIcon = Boolean(children) || ariaLabel !== undefined;

  return (
    <StyledButtonOrLink
      data={data}
      id={id}
      className={[
        className,
        variant,
        size ?? 'medium',
        fullWidth && 'full-width',
        noPadding && 'no-padding',
        noBold && 'no-bold',
        rounded && 'rounded',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ width, height, ...style }}
      title={title}
      tabIndex={tabIndex}
      qaId={qaId}
      aria-label={ariaLabel}
    >
      {icon && (
        <ButtonIconWrapper
          $position={
            (typeof icon === 'object' && 'position' in icon && icon.position) ||
            'left'
          }
        >
          {typeof icon === 'object' && 'name' in icon ? (
            <Icon
              aria-hidden={hideObjectIcon || undefined}
              colorName={icon.color}
              name={icon.name}
              size={icon.size ?? 20}
            />
          ) : (
            icon
          )}
        </ButtonIconWrapper>
      )}

      {children && <div style={{ display: 'inline-flex' }}>{children}</div>}
    </StyledButtonOrLink>
  );
}
